import React, { useState, useMemo } from "react";
import { useQuery, gql } from "@apollo/client";
import {
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiDownload,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
  FiTrendingUp,
  FiAlertCircle,
  FiCalendar,
  FiUser,
  FiMail,
  FiPhone,
  FiFileText,
  FiBriefcase,
  FiClock,
  FiEye
} from "react-icons/fi";
import "../styles/JobApplicationView.css";

const GET_JOB_APPLICATIONS = gql`
  query GetJobApplications {
    getJobApplications {
      id
      buyerId
      jobId
      name
      email
      phoneNumber
      resumeUrl
      status
      appliedAt
      createdAt
    }
  }
`;

export default function JobApplicationsList() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [jobFilter, setJobFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);

  const { loading, error, data, refetch } = useQuery(GET_JOB_APPLICATIONS, {
    fetchPolicy: "network-only",
  });

  const applications = data?.getJobApplications || [];

  // Apply filters
  const processedApplications = useMemo(() => {
    let results = [...applications];
    
    // Search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      results = results.filter(app => 
        app.name?.toLowerCase().includes(term) ||
        app.email?.toLowerCase().includes(term) ||
        app.phoneNumber?.toLowerCase().includes(term) ||
        app.buyerId?.toLowerCase().includes(term) ||
        app.jobId?.toLowerCase().includes(term) ||
        app.id?.toLowerCase().includes(term)
      );
    }
    
    // Status filter
    if (statusFilter !== "all") {
      results = results.filter(app => app.status?.toLowerCase() === statusFilter);
    }
    
    // Date filter
    if (dateFilter) {
      results = results.filter(app => {
        const applicationDate = parseDate(app.appliedAt || app.createdAt);
        if (!applicationDate) return false;
        const filterDate = new Date(dateFilter);
        return applicationDate.toDateString() === filterDate.toDateString();
      });
    }
    
    // Job filter
    if (jobFilter) {
      results = results.filter(app => 
        app.jobId?.toLowerCase().includes(jobFilter.toLowerCase())
      );
    }
    
    return results;
  }, [applications, searchTerm, statusFilter, dateFilter, jobFilter]);

  const parseDate = (value) => {
    if (!value) return null;
    if (!isNaN(value)) {
      if (value.toString().length === 10) return new Date(value * 1000);
      return new Date(Number(value));
    }
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  };

  const formatDate = (dateValue) => {
    const date = parseDate(dateValue);
    if (!date) return "N/A";
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateValue) => {
    const date = parseDate(dateValue);
    if (!date) return "N/A";
    return date.toLocaleString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Statistics
  const stats = useMemo(() => {
    const total = applications.length;
    const pending = applications.filter(app => app.status?.toLowerCase() === "pending").length;
    const reviewed = applications.filter(app => app.status?.toLowerCase() === "reviewed").length;
    const shortlisted = applications.filter(app => app.status?.toLowerCase() === "shortlisted").length;
    const rejected = applications.filter(app => app.status?.toLowerCase() === "rejected").length;
    
    return { total, pending, reviewed, shortlisted, rejected };
  }, [applications]);

  // Pagination
  const totalPages = Math.ceil(processedApplications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = processedApplications.slice(startIndex, endIndex);

  const resetFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setDateFilter("");
    setJobFilter("");
    setCurrentPage(1);
  };

  const handleExport = () => {
    const headers = [
      "Application ID",
      "Name",
      "Email",
      "Phone",
      "Buyer ID",
      "Job ID",
      "Status",
      "Applied At",
      "Created At"
    ];

    const rows = processedApplications.map(app => [
      app.id,
      `"${app.name || ""}"`,
      `"${app.email || ""}"`,
      `"${app.phoneNumber || ""}"`,
      `"${app.buyerId || ""}"`,
      `"${app.jobId || ""}"`,
      app.status?.charAt(0).toUpperCase() + app.status?.slice(1),
      formatDate(app.appliedAt),
      formatDateTime(app.createdAt)
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    
    link.setAttribute("href", url);
    link.setAttribute("download", `job-applications-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return (
    <div className="booking-loading">
      <div className="premium-spinner"></div>
      <p>Loading applications...</p>
    </div>
  );
  
  if (error) return (
    <div className="booking-error">
      <div className="error-icon">
        <FiAlertCircle />
      </div>
      <p>Error loading data</p>
      <button onClick={() => refetch()} className="retry-btn">
        Try Again
      </button>
    </div>
  );

  return (
    <div className="booking-container premium">
      {/* Premium Header */}
      <div className="premium-header">
        <div className="header-main">
          <h1 className="premium-title">
            <FiTrendingUp className="title-icon" />
            Job Applications
          </h1>
          <p className="premium-subtitle">Manage and track all job applications</p>
        </div>
        
        <div className="header-stats">
          <div className="stat-mini total">
            <div className="stat-mini-value">{stats.total}</div>
            <div className="stat-mini-label">Total</div>
          </div>
          <div className="stat-mini pending">
            <div className="stat-mini-value">{stats.pending}</div>
            <div className="stat-mini-label">Pending</div>
          </div>
          <div className="stat-mini reviewed">
            <div className="stat-mini-value">{stats.reviewed}</div>
            <div className="stat-mini-label">Reviewed</div>
          </div>
          <div className="stat-mini shortlisted">
            <div className="stat-mini-value">{stats.shortlisted}</div>
            <div className="stat-mini-label">Shortlisted</div>
          </div>
          <div className="stat-mini rejected">
            <div className="stat-mini-value">{stats.rejected}</div>
            <div className="stat-mini-label">Rejected</div>
          </div>
        </div>
        
        <div className="header-controls">
          <button className="premium-btn export" onClick={handleExport}>
            <FiDownload /> Export
          </button>
          <button className="premium-btn refresh" onClick={() => refetch()}>
            <FiRefreshCw />
          </button>
        </div>
      </div>

      {/* Premium Filter Bar */}
      <div className="premium-filter-bar">
        <div className="search-wrapper">
          <div className="search-icon">
            <FiSearch />
          </div>
          <input
            type="text"
            placeholder="Search applications..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="premium-search"
          />
          {searchTerm && (
            <button className="clear-btn" onClick={() => setSearchTerm("")}>
              <FiX />
            </button>
          )}
        </div>
        
        <div className="filter-controls">
          <button 
            className={`filter-btn ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FiFilter /> Filter
          </button>
          <div className="page-control">
            <select 
              value={itemsPerPage} 
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="premium-select"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <div className="premium-advanced-filters">
          <div className="filter-row">
            <div className="filter-col">
              <label>Status</label>
              <select 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value)}
                className="premium-select"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
                <option value="shortlisted">Shortlisted</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            
            <div className="filter-col">
              <label>Date</label>
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="premium-select"
              />
            </div>
            
            <div className="filter-col">
              <label>Job ID</label>
              <input
                type="text"
                placeholder="Job ID"
                value={jobFilter}
                onChange={(e) => setJobFilter(e.target.value)}
                className="premium-select"
              />
            </div>
            
            <div className="filter-col">
              <button className="reset-filters" onClick={resetFilters}>
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Premium Table */}
      <div className="premium-table-container">
        <div className="table-info-bar">
          <div className="table-count">
            {processedApplications.length} applications
          </div>
          <div className="table-navigation">
            Showing {Math.min(startIndex + 1, processedApplications.length)}-{Math.min(endIndex, processedApplications.length)} of {processedApplications.length}
          </div>
        </div>
        
        <div className="table-scroll-wrapper">
          <table className="premium-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Applicant</th>
                <th>Contact</th>
                <th>Job & Buyer</th>
                <th>Status</th>
                <th>Applied Date</th>
                <th>Created</th>
                <th>Resume</th>
              </tr>
            </thead>
            
            <tbody>
              {currentItems.length === 0 ? (
                <tr>
                  <td colSpan="8" className="empty-state">
                    <FiAlertCircle />
                    <p>No applications found</p>
                  </td>
                </tr>
              ) : (
                currentItems.map((app, index) => (
                  <tr 
                    key={app.id} 
                    className="booking-row"
                    onClick={() => setSelectedApplication(app)}
                  >
                    <td className="row-number">
                      {startIndex + index + 1}
                    </td>
                    <td>
                      <div className="customer-info">
                        <div className="customer-name">
                          <FiUser /> {app.name || "—"}
                        </div>
                        <div className="customer-email">
                          <FiMail /> {app.email || "—"}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="phone-info">
                        <div className="phone">
                          <FiPhone /> {app.phoneNumber || "—"}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="job-info">
                        <div className="job-id">
                          <FiBriefcase /> Job: {app.jobId || "—"}
                        </div>
                        <div className="buyer-id">
                          <FiUser /> Buyer: {app.buyerId || "—"}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="status-cell">
                        <span className={`status-badge ${app.status?.toLowerCase()}`}>
                          {app.status?.charAt(0).toUpperCase() + app.status?.slice(1) || "—"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="date-info">
                        <div className="date">
                          <FiCalendar /> {formatDate(app.appliedAt)}
                        </div>
                        <div className="date-time">
                          <FiClock /> {formatDateTime(app.appliedAt)}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="created">
                        {formatDateTime(app.createdAt)}
                      </div>
                    </td>
                    <td>
                      <div className="resume-cell">
                        {app.resumeUrl ? (
                          <a
                            href={app.resumeUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="resume-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <FiFileText /> View
                          </a>
                        ) : (
                          <span className="no-resume">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Premium Pagination */}
        {totalPages > 1 && (
          <div className="premium-pagination">
            <div className="pagination-info">
              Page {currentPage} of {totalPages}
            </div>
            <div className="pagination-nav">
              <button 
                className="nav-btn first"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                <FiChevronsLeft />
              </button>
              <button 
                className="nav-btn prev"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <FiChevronLeft />
              </button>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return pageNum <= totalPages ? (
                  <button
                    key={pageNum}
                    className={`nav-btn ${currentPage === pageNum ? 'active' : ''}`}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                ) : null;
              })}
              
              <button 
                className="nav-btn next"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                <FiChevronRight />
              </button>
              <button 
                className="nav-btn last"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                <FiChevronsRight />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Premium Modal */}
      {selectedApplication && (
        <div className="premium-modal-overlay" onClick={() => setSelectedApplication(null)}>
          <div className="premium-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Application Details</h3>
              <button className="modal-close" onClick={() => setSelectedApplication(null)}>
                <FiX />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="detail-section">
                <div className="detail-row">
                  <span className="detail-label">Application ID</span>
                  <span className="detail-value">#{selectedApplication.id}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Applicant</span>
                  <span className="detail-value">
                    <FiUser /> {selectedApplication.name}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Email</span>
                  <span className="detail-value">
                    <FiMail /> {selectedApplication.email}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Phone</span>
                  <span className="detail-value">
                    <FiPhone /> {selectedApplication.phoneNumber}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Buyer ID</span>
                  <span className="detail-value">{selectedApplication.buyerId || "—"}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Job ID</span>
                  <span className="detail-value">{selectedApplication.jobId || "—"}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Status</span>
                  <span className={`detail-value status ${selectedApplication.status?.toLowerCase()}`}>
                    {selectedApplication.status?.charAt(0).toUpperCase() + selectedApplication.status?.slice(1)}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Applied At</span>
                  <span className="detail-value">
                    <FiCalendar /> {formatDateTime(selectedApplication.appliedAt)}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Created At</span>
                  <span className="detail-value">{formatDateTime(selectedApplication.createdAt)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Resume</span>
                  <span className="detail-value">
                    {selectedApplication.resumeUrl ? (
                      <a
                        href={selectedApplication.resumeUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="resume-link"
                      >
                        <FiFileText /> View Resume
                      </a>
                    ) : (
                      <span className="no-resume">No resume attached</span>
                    )}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="modal-action-btn" onClick={() => setSelectedApplication(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}